import { type ValidationErrors } from 'final-form';
import { DateTime, Interval } from 'luxon';

/**
 * @param date Date to validate
 * @param minAge Minimum age in years
 * @param maxAge Maximum age in years
 */
export function isAgeOutOfRange(
  dateOfBirth: Date,
  minAge: number,
  maxAge: number
): boolean {
  const d0 = DateTime.fromJSDate(dateOfBirth).startOf('day');
  const d1 = DateTime.fromISO(
    window.__RHVKLSO002_INITIAL_STATE.currentDate
  ).startOf('day');
  const years = Math.floor(Interval.fromDateTimes(d0, d1).length('years'));
  if (!years) {
    return true;
  }
  return years < minAge || years > maxAge;
}

export function focusFirstError(_errors: ValidationErrors): void {
  if (_errors) {
    for (const [key, value] of Object.entries(_errors)) {
      if (value) {
        getFirstFocusable(document.getElementById(key))?.focus();
        break;
      }
    }
  }
}

export function getFirstFocusable(ele: HTMLElement | null): HTMLElement | null {
  if (isFocusable(ele)) return ele;
  else if (!ele?.hasAttribute('tabIndex') && ele?.childElementCount) {
    for (const child of ele.children) {
      if (getFirstFocusable(child as HTMLElement))
        return getFirstFocusable(child as HTMLElement);
    }
  }
  return null;
}

export function required(value: any): string | undefined {
  return !value ? 'common.required' : undefined;
}

export function isFocusable(element: HTMLElement | null): boolean {
  if (!element) return false;

  if ((element as HTMLInputElement).disabled) return false;

  const focusableTags: string[] = [
    'A',
    'BUTTON',
    'INPUT',
    'TEXTAREA',
    'SELECT',
    'DETAILS',
  ];
  const tagName: string = element.tagName.toUpperCase();

  if (focusableTags.includes(tagName)) {
    if (tagName === 'A' && !element.hasAttribute('href')) return false;
    if (tagName === 'INPUT' && (element as HTMLInputElement).type === 'hidden')
      return false;
    return true;
  }

  // Any element with tabindex is focusable
  if (element.hasAttribute('tabindex')) {
    return true;
  }

  if (element.hasAttribute('contenteditable')) {
    return element.getAttribute('contenteditable') !== 'false';
  }

  return false;
}
